import { useCallback } from "react";

import { SocialMedia } from "../../utils/SocialMedia";
import { CUSTOMER_ANALYTICS_EVENT } from "../../utils/enums";
import { getEventSourceUrl } from "../../utils/help/socialMediaPixels/metaClickIds";
import { createMetaEventId } from "../../utils/help/socialMediaPixels/metaEventId";
import { useAppSelector } from "./useReduxHooks";
import { useCustomerAnalytics } from "./useCustomerAnalytics";

type AddToCartTrackingInput = {
  subItemId: number;
  subItemName?: string;
  quantity: number;
  totalPrice: number;
  itemId?: number;
};

/**
 * Reports one genuine add-to-cart to both Meta paths under a single shared id.
 *
 * The two calls have to agree on `metaEventId` or Meta counts the browser and
 * Conversions API copies as two separate AddToCarts. Keeping the mint and both
 * sends in one place is what guarantees that: the alternative is threading an id
 * between an analytics call and a pixel call that sit ~90 lines apart in
 * `ItemContext`, repeated at four call sites.
 *
 * Only call this for a genuine add. Quantity changes must not report an
 * AddToCart, and those go through `editItem`/`editLineQuantity` rather than here.
 */
export const useAddToCartTracking = () => {
  const addCustomerAnalytics = useCustomerAnalytics();
  const preferredCountry = useAppSelector(
    (state) => state.config.restaurantInfo?.preferredCountry
  );

  return useCallback(
    (input: AddToCartTrackingInput) => {
      const metaEventId = createMetaEventId();

      // Not awaited: the hook's callers are click handlers, and
      // `addCustomerAnalytics` already swallows its own failures. Awaiting would
      // put a network round trip in front of the pixel for no benefit -- the id
      // is minted before either call, so ordering cannot affect deduplication.
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ADD_TO_CART, {
        subitemId: input.subItemId,
        subitemName: input.subItemName,
        price: input.totalPrice,
        quantity: input.quantity,
        itemId: input.itemId,
        metaEventId,
        eventSourceUrl: getEventSourceUrl(),
      });

      SocialMedia.addToCartEvent(
        {
          subItemId: input.subItemId,
          fullName: input.subItemName,
          quantity: input.quantity,
          totalPrice: input.totalPrice,
        },
        preferredCountry,
        metaEventId
      );
    },
    [addCustomerAnalytics, preferredCountry]
  );
};
